import type {Theme} from '@emotion/react';

/**
 * Defines the available visual variants for a button component.
 * This type limits the button's appearance to one of three predefined variants:
 * - `'outlined'` — A button with a transparent background and a visible border, often used for secondary actions.
 * - `'primary'` — The main action button, usually styled with a prominent background color to draw attention.
 * - `'secondary'` — A supporting action button, styled to be less visually dominant than the primary button.
 * Using this type ensures consistent button styling and prevents invalid variant values from being assigned.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

/**
 * Defines the available width options for a button component.
 * This type restricts the button width to one of two options:
 * - `'auto'` — The button width adjusts automatically based on its content.
 * - `'full'` — The button expands to take up the full available width of its container.
 * Using this type ensures consistent width behavior across button components and
 * prevents invalid width values from being assigned.
 */
export type ButtonWidth = 'auto' | 'full';

/**
 * The styling-related properties used for determining a button's visual appearance.
 * This interface defines the expected properties that control button styling in
 * styled components or similar CSS-in-JS solutions. It includes the chosen style
 * (light or dark), variant (outlined, primary, or secondary), and the theme object
 * used for consistent design tokens across the application.
 */
interface ButtonColorProps {
    /**
     * The visual variant of the button, such as `'outlined'`, `'primary'`, or `'secondary'`.
     * This affects how the button is rendered in terms of background, border, and emphasis.
     */
    $variant: ButtonVariant;
    /**
     * The theme object containing global design tokens like colors, typography, and spacing.
     * Used to ensure consistent styling across all components.
     */
    theme: Theme;
}

/**
 * Determines the default background color for a button based on its variant.
 * This utility ensures consistent styling by mapping button variants to colors
 * defined in the theme. Secondary buttons receive a light neutral background,
 * while all other variants default to a darker neutral.
 *
 * @param props          The properties object containing variant and theme information.
 * @param props.$variant The button variant (e.g., `'secondary'`, `'primary'`).
 * @param props.theme    The theme object containing the color palette.
 * @returns The resolved background color string from the theme.
 *
 * @example
 * ```tsx
 * const bgColor = getButtonBackgroundColor({ $variant: 'secondary', theme });
 * ```
 */
export const getButtonBackgroundColor = ({$variant, theme}: ButtonColorProps) => {
    if ($variant === 'secondary') {
        return theme.colors.neutral100;
    }

    if ($variant === 'tertiary') {
        return theme.colors.neutral1000;
    }

    return theme.colors.neutral800;
};

/**
 * Determines the hover background color for a button based on its variant.
 * This utility provides hover-state styling consistent with the button's variant.
 * Secondary buttons receive a lighter neutral background on hover, while all other
 * variants use a slightly lighter dark neutral for hover feedback.
 *
 * @param props          The properties object containing variant and theme information.
 * @param props.$variant The button variant (e.g., `'secondary'`, `'primary'`).
 * @param props.theme    The theme object containing the color palette.
 * @returns The resolved hover background color string from the theme.
 *
 * @example
 * ```tsx
 * const hoverColor = getButtonBackgroundHoverColor({ $variant: 'primary', theme });
 * ```
 */
export const getButtonBackgroundHoverColor = ({$variant, theme}: ButtonColorProps) => {
    if ($variant === 'secondary') {
        return theme.colors.neutral200;
    }

    if ($variant === 'tertiary') {
        return theme.colors.neutral800;
    }

    return theme.colors.neutral700;
};

/**
 * Determines the font (text) color for a button based on its variant.
 * This utility ensures proper text contrast against the button's background.
 * Secondary buttons use the theme’s primary text color on a light background,
 * while all other variants use the inverted primary text color for readability.
 *
 * @param props          The properties object containing variant and theme information.
 * @param props.$variant The button variant (e.g., `'secondary'`, `'primary'`).
 * @param props.theme    The theme object containing the color palette.
 * @returns The resolved font color string from the theme.
 *
 * @example
 * ```tsx
 * const fontColor = getButtonFontColor({ $variant: 'secondary', theme });
 * ```
 */
export const getButtonFontColor = ({$variant, theme}: ButtonColorProps) => {
    if ($variant === 'secondary') {
        return theme.colors.textPrimary;
    }

    if ($variant === 'tertiary') {
        return theme.colors.textPrimaryInverted;
    }

    return theme.colors.textPrimaryInverted;
};